import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { some, isEmpty, uniq, without, flatten, range, sortBy, sum } from 'lodash-es';
import { useMatch } from '../../contexts/MatchContext';
import * as api from '../../api';
import { getScoresWithPoints, getAllDivisions, getAllCategories, pad, getFlagSrc } from '../../utils/scoring';

export default function Results() {
  const { t } = useTranslation();
  const { match } = useMatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [scores, setScores] = useState(null);
  const [shooters, setShooters] = useState(null);
  const [stages, setStages] = useState(null);
  const [scoreType, setScoreType] = useState('overall');
  const [division, setDivision] = useState(null);
  const [category, setCategory] = useState(null);
  const [stage, setStage] = useState(0);
  const [teamType, setTeamType] = useState(null);

  const whatIfParam = searchParams.get('whatIf');
  const whatIfOverrideParam = searchParams.get('whatIfOverride');
  const whatIfShooterId = searchParams.get('shooterId');
  const whatIfScores = useMemo(() => {
    if (!whatIfParam) return [];
    try { return JSON.parse(decodeURIComponent(escape(atob(whatIfParam)))); } catch { return []; }
  }, [whatIfParam]);
  const whatIfOverride = useMemo(() => {
    if (!whatIfOverrideParam) return null;
    try { return JSON.parse(decodeURIComponent(escape(atob(whatIfOverrideParam)))); } catch { return null; }
  }, [whatIfOverrideParam]);
  const hasWhatIf = whatIfScores.length > 0 || !!whatIfOverride;

  const [refreshing, setRefreshing] = useState(false);

  const loadData = React.useCallback(async () => {
    if (!match) return;
    const [scoresRes, shootersRes, stagesRes] = await Promise.all([
      api.getScores(match.id),
      api.getShooters(match.id),
      match.stages?.length > 0 ? api.getStages(match.stages.join(',')) : Promise.resolve({ data: [] }),
    ]);
    setScores(scoresRes.data?.scores || scoresRes.data || []);
    setShooters((shootersRes.data || []).filter((s) => !s.cancelledParticipation));
    setStages(stagesRes.data || []);
  }, [match?.id]);

  useEffect(() => {
    if (!match) return;
    window.scrollTo(0, 0);
    if (match.scoresPublished === 'verify-only') {
      setScoreType('verify');
    }
    loadData();
  }, [match?.id, loadData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const computed = useMemo(() => {
    if (!scores || !shooters || !stages) return null;

    const allScores = whatIfScores.length > 0 ? [...scores, ...whatIfScores] : scores;
    const effShooters = whatIfOverride
      ? shooters.map((s) => (s.id === whatIfOverride.shooterId
        ? { ...s, ...(whatIfOverride.division ? { division: whatIfOverride.division } : {}), ...(whatIfOverride.powerFactor ? { 'power-factor': whatIfOverride.powerFactor } : {}) }
        : s))
      : shooters;

    const data = getScoresWithPoints({ scores: allScores, shooters: effShooters, stages, mode: match.scoreMode });
    const combinedData = getScoresWithPoints({ scores: allScores, shooters: effShooters, stages, combined: true, mode: match.scoreMode });

    const divs = uniq(data.scoresOverall.map((s) => s.division));
    const cats = without(uniq(data.scoresOverall.map((s) => s.category)), 'regular');

    return {
      ...data,
      combinedScoresByStage: combinedData.scoresByStage,
      combinedScoresOverall: combinedData.scoresOverall,
      divisions: isEmpty(divs) ? getAllDivisions({ matchType: match.type }) : divs,
      categories: isEmpty(cats) ? getAllCategories({ matchType: match.type }) : cats,
    };
  }, [scores, shooters, stages, whatIfScores, whatIfOverride]);

  useEffect(() => {
    if (computed?.divisions?.length > 0 && !division) {
      setDivision(computed.divisions[0]);
    }
  }, [computed?.divisions]);

  useEffect(() => {
    if (match?.teams?.length > 0 && !teamType) {
      setTeamType(match.teams[0].type);
    }
  }, [match?.teams]);

  if (!computed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const { scoresByStage, scoresOverall, combinedScoresByStage, combinedScoresOverall, dqs, divisions, categories } = computed;

  const activeStageCount = stages.filter((s) => !s.inactive).length;
  const stagesPerShooter = {};
  scoresByStage.forEach((stageScores, idx) => {
    if (stages[idx]?.inactive) return;
    stageScores.forEach((s) => { stagesPerShooter[s.shooterId] = (stagesPerShooter[s.shooterId] || 0) + 1; });
  });

  if (isEmpty(scoresOverall) && dqs.length === 0) {
    return (
      <Typography variant="body1" sx={{ textAlign: 'center', mt: 4, width: '90%', mx: 'auto' }}>
        {t('results.noScores')}
      </Typography>
    );
  }

  const combined = scoreType === 'combined' || scoreType === 'combined-by-stage';

  const PublicId = ({ publicId, name, isRo }) => {
    const parts = (publicId || '').split('|');
    const displayId = parts[parts.length - 1];
    const countryCode = parts.length > 1 ? parts[0] : null;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {countryCode && (
          <img alt={countryCode} src={getFlagSrc({ countryCode, size: 16 })} style={{ display: 'block' }} />
        )}
        <span>{displayId}</span>
        <span style={{ margin: '0 3px' }}>/</span>
        <span>{name}</span>
        {isRo && <Chip label="RO" size="small" color="primary" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />}
      </Box>
    );
  };

  const toDivisionKey = (div) => {
    const map = {
      'standard': 'standard', 'production': 'production', 'open': 'open',
      'production-optics': 'productionOptics', 'optics': 'optics', 'classic': 'classic',
      'revolver': 'revolver', 'pcc-optics': 'pccOptics', 'pcc-iron': 'pccIron',
    };
    return map[div] || div;
  };

  const toCategoryKey = (cat) => {
    const map = {
      'regular': 'regular', 'senior': 'senior', 'super-senior': 'superSenior',
      'grand-senior': 'grandSenior', 'lady-senior': 'ladySenior', 'junior': 'junior',
      'super-junior': 'superJunior', 'grand-junior': 'grandJunior', 'lady-junior': 'ladyJunior',
      'lady-super-junior': 'ladySuperJunior', 'lady-grand-junior': 'ladyGrandJunior', 'lady': 'lady',
    };
    return map[cat] || cat;
  };

  const handleExportCsv = () => {
    const csvSafe = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const data = combined ? combinedScoresOverall : scoresOverall;
    const dqIds = new Set(dqs.map((dq) => dq.shooterId));
    const divList = combined ? ['combined'] : (division === 'all' ? divisions : [division]);
    const rows = [];
    rows.push([
      t('results.position', 'Pos'),
      t('shooter.division'),
      t('shooter.category'),
      t('shooter.id'),
      t('shooter.name'),
      '%',
      t('results.totalPoints'),
    ].map(csvSafe).join(','));
    divList.forEach((d) => {
      const filtered = data.filter((s) => combined || s.division === d);
      let pos = 0;
      filtered.forEach((s) => {
        if (dqIds.has(s.shooterId)) return;
        pos += 1;
        rows.push([
          pos,
          combined ? '' : t(`divisions.${toDivisionKey(d)}`, { defaultValue: d }),
          s.category && s.category !== 'regular' ? t(`categories.${toCategoryKey(s.category)}`, { defaultValue: s.category }) : '',
          s.shooterPublicId || '',
          s.shooterName || '',
          pad(s.percentage / 100, 2),
          pad(s.score, 4),
        ].map(csvSafe).join(','));
      });
    });
    dqs.forEach((dq) => {
      rows.push(['DQ', '', '', dq.shooterPublicId || '', dq.shooterName || '', '', ''].map(csvSafe).join(','));
    });
    const csv = '﻿' + rows.join('\n');
    const safeTitle = (match?.title || 'results').replace(/[^A-Za-z0-9_-]+/g, '_');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeTitle}-results.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderOverall = () => {
    const data = combined ? combinedScoresOverall : scoresOverall;
    const divisionsList = combined ? [null] : divisions;

    return divisionsList.map((d) => (
      <Box
        key={d || 'combined'}
        sx={{
          display: (combined || division === 'all' || d === division) ? 'block' : 'none',
          pageBreakBefore: 'always',
          pb: 3,
          overflowX: 'auto',
        }}
      >
        <Typography variant="h5" gutterBottom>
          {combined
            ? t('results.combinedTitle')
            : t('results.overallTitle', { division: t(`divisions.${toDivisionKey(d)}`, { defaultValue: d }) })}
        </Typography>
        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <td style={{ padding: '6px' }} />
              <td style={{ padding: '6px', textAlign: 'center' }}><b>%</b></td>
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.totalPoints')}</b></td>
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.name')}</b></td>
              {combined && <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.division')}</b></td>}
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.category')}</b></td>
            </tr>
          </thead>
          <tbody>
            {data.filter((s) => combined || s.division === d).map((score, index) => {
              if (some(dqs, (dq) => dq.shooterId === score.shooterId)) return null;
              return (
                <tr key={score.shooterId}>
                  <td style={{ padding: '12px' }}>{index + 1}</td>
                  <td style={{ padding: '12px' }}>{pad(score.percentage / 100, 2)}%</td>
                  <td style={{ padding: '12px' }}>{pad(score.score, 4)}</td>
                  <td style={{ padding: '12px' }}>
                    <PublicId publicId={score.shooterPublicId} name={score.shooterName} isRo={score.isRo} />
                    {activeStageCount > 1 && (stagesPerShooter[score.shooterId] || 0) < activeStageCount && (
                      <Typography variant="caption" color="text.secondary">
                        ({stagesPerShooter[score.shooterId] || 0}/{activeStageCount})
                      </Typography>
                    )}
                  </td>
                  {combined && <td style={{ padding: '12px' }}>{t(`divisions.${toDivisionKey(score.division)}`, { defaultValue: score.division })}</td>}
                  <td style={{ padding: '12px' }}>{score.category !== 'regular' ? t(`categories.${toCategoryKey(score.category)}`, { defaultValue: score.category }) : ''}</td>
                </tr>
              );
            })}
            {dqs.filter((dq) => combined || dq.division === d).map((dq) => (
              <tr key={`dq-${dq.shooterId}`} style={{ backgroundColor: '#ffebee' }}>
                <td style={{ padding: '12px' }}>DQ</td>
                <td style={{ padding: '12px' }}>DQ</td>
                <td style={{ padding: '12px' }}>-</td>
                <td style={{ padding: '12px' }}>
                  <PublicId publicId={dq.shooterPublicId} name={dq.shooterName} isRo={dq.isRo} />
                </td>
                {combined && <td style={{ padding: '12px' }}>{t(`divisions.${toDivisionKey(dq.division)}`, { defaultValue: dq.division })}</td>}
                <td style={{ padding: '12px' }}>{dq.category !== 'regular' ? t(`categories.${toCategoryKey(dq.category)}`, { defaultValue: dq.category }) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    ));
  };

  const renderByStage = () => {
    const data = combined ? combinedScoresByStage : scoresByStage;
    const divisionsList = combined ? [null] : divisions;

    return flatten(divisionsList.map((d) => range(stages.length).map(($stage) => {
      if (stage !== 'all' && $stage !== stage) return null;
      if (stages[$stage]?.inactive) {
        return (
          <Box
            key={`${d}.${$stage}`}
            sx={{
              display: (combined || division === 'all' || d === division) ? 'block' : 'none',
              pageBreakBefore: 'always',
              pb: 3,
            }}
            className="noprint"
          >
            <Typography variant="h5" gutterBottom>
              {combined
                ? t('results.combinedByStageTitle', { stage: $stage + 1 })
                : t('results.byStageTitle', { stage: $stage + 1, division: t(`divisions.${toDivisionKey(d)}`, { defaultValue: d }) })}
            </Typography>
            <Typography variant="body2" color="error">{t('results.inactive')}</Typography>
          </Box>
        );
      }

      const stageData = data[$stage] || [];
      const sorted = sortBy(
        stageData.filter((s) => combined || s.division === d),
        (s) => -1 * s.factor
      );
      const shooterIdsInStage = new Set(stageData.map((s) => s.shooterId));
      const dqIds = new Set(dqs.map((dq) => dq.shooterId));
      const overallList = combined ? combinedScoresOverall : scoresOverall;
      const notShotYet = sortBy(
        overallList.filter((s) => (combined || s.division === d)
          && !shooterIdsInStage.has(s.shooterId)
          && !dqIds.has(s.shooterId)),
        (s) => (s.shooterName || '').toLowerCase(),
      );

      return (
        <Box
          key={`${d}.${$stage}`}
          sx={{
            display: (combined || division === 'all' || d === division) ? 'block' : 'none',
            pageBreakBefore: 'always',
            pb: 3,
            overflowX: 'auto',
          }}
        >
          <Typography variant="h5" gutterBottom>
            {combined
              ? t('results.combinedByStageTitle', { stage: $stage + 1 })
              : t('results.byStageTitle', { stage: $stage + 1, division: t(`divisions.${toDivisionKey(d)}`, { defaultValue: d }) })}
          </Typography>
          <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <td style={{ padding: '6px' }} />
                <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.totalPoints')}</b></td>
                <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.time')}</b></td>
                <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.factor')}</b></td>
                <td style={{ padding: '6px', textAlign: 'center' }}><b>%</b></td>
                <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.stagePoints')}</b></td>
                <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.name')}</b></td>
                {combined && <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.division')}</b></td>}
                <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.category')}</b></td>
              </tr>
            </thead>
            <tbody>
              {sorted.map((score, index) => {
                if (some(dqs, (dq) => dq.shooterId === score.shooterId)) return null;
                return (
                  <tr key={score.shooterId}>
                    <td style={{ padding: '12px' }}>{index + 1}</td>
                    <td style={{ padding: '6px' }}>{score.total}</td>
                    <td style={{ padding: '6px' }}>{pad(score.time, 2)}</td>
                    <td style={{ padding: '6px' }}>{pad(score.factor, 4)}</td>
                    <td style={{ padding: '12px' }}>{pad(score.percentage / 100, 2)}%</td>
                    <td style={{ padding: '12px' }}>{pad(score.points, 4)}</td>
                    <td style={{ padding: '12px' }}>
                      <PublicId publicId={score.shooterPublicId} name={score.shooterName} isRo={score.isRo} />
                    </td>
                    {combined && <td style={{ padding: '12px' }}>{t(`divisions.${toDivisionKey(score.division)}`, { defaultValue: score.division })}</td>}
                    <td style={{ padding: '12px' }}>{score.category !== 'regular' ? t(`categories.${toCategoryKey(score.category)}`, { defaultValue: score.category }) : ''}</td>
                  </tr>
                );
              })}
              {notShotYet.map((s) => (
                <tr key={`ns-${s.shooterId}`} style={{ backgroundColor: '#fafafa', color: '#888' }}>
                  <td style={{ padding: '12px' }}>—</td>
                  <td style={{ padding: '6px' }} colSpan={5}>{t('results.notShotStage')}</td>
                  <td style={{ padding: '12px' }}>
                    <PublicId publicId={s.shooterPublicId} name={s.shooterName} isRo={s.isRo} />
                  </td>
                  {combined && <td style={{ padding: '12px' }}>{t(`divisions.${toDivisionKey(s.division)}`, { defaultValue: s.division })}</td>}
                  <td style={{ padding: '12px' }}>{s.category !== 'regular' ? t(`categories.${toCategoryKey(s.category)}`, { defaultValue: s.category }) : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      );
    })));
  };

  const renderByCategory = () => {
    const selectedCategory = category || categories[0];
    const filteredData = scoresOverall.filter((s) =>
      (division === 'all' || s.division === division) &&
      (selectedCategory === 'all' || s.category === selectedCategory)
    );

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Typography variant="h5" gutterBottom>
          {t(`divisions.${toDivisionKey(division)}`, { defaultValue: division })} - {t(`categories.${toCategoryKey(selectedCategory)}`, { defaultValue: selectedCategory })}
        </Typography>
        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <td style={{ padding: '6px' }} />
              <td style={{ padding: '6px', textAlign: 'center' }}><b>%</b></td>
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.totalPoints')}</b></td>
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.name')}</b></td>
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('shooter.category')}</b></td>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((score, index) => {
              if (some(dqs, (dq) => dq.shooterId === score.shooterId)) return null;
              return (
                <tr key={score.shooterId}>
                  <td style={{ padding: '12px' }}>{index + 1}</td>
                  <td style={{ padding: '12px' }}>{pad(score.percentage / 100, 2)}%</td>
                  <td style={{ padding: '12px' }}>{pad(score.score, 4)}</td>
                  <td style={{ padding: '12px' }}>
                    <PublicId publicId={score.shooterPublicId} name={score.shooterName} isRo={score.isRo} />
                    {activeStageCount > 1 && (stagesPerShooter[score.shooterId] || 0) < activeStageCount && (
                      <Typography variant="caption" color="text.secondary">
                        &nbsp;({stagesPerShooter[score.shooterId] || 0}/{activeStageCount})
                      </Typography>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>{score.category !== 'regular' ? t(`categories.${toCategoryKey(score.category)}`, { defaultValue: score.category }) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    );
  };

  const renderVerify = () => {
    if (!stages || stages.length === 0) return null;

    // Count all non-deleted scores per shooter+stage for edit indicators
    const scoreCountMap = {};
    (scores || []).forEach((s) => {
      if (s.deleted) return;
      const key = `${s.shooterId}_${s.stageIdx}`;
      scoreCountMap[key] = (scoreCountMap[key] || 0) + 1;
    });

    // Build unique shooter list sorted by publicId number (matching old system)
    const allShooterIds = uniq(scoresOverall.map((s) => s.shooterId));
    const sortedShooterIds = sortBy(allShooterIds, (shooterId) => {
      const s = scoresOverall.find((sc) => sc.shooterId === shooterId);
      const parts = (s?.shooterPublicId || '').split('|');
      return parseInt(parts[parts.length - 1], 10) || 0;
    });

    const scoreMode = match.scoreMode;
    const cHeader = scoreMode === 'comstock-abd' ? 'B' : 'C';
    const dHeader = scoreMode === 'il-army' ? '' : 'D';
    const cellStyle = { padding: '6px', textAlign: 'center' };

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          {t('results.verifyTitle')}
        </Typography>
        {sortedShooterIds.map((shooterId) => {
          // Collect this shooter's score per stage
          const perStage = scoresByStage.map((s) => s.find(($s) => $s.shooterId === shooterId));
          const anyScore = perStage.find((s) => !!s);
          if (!anyScore) return null;
          if (some(dqs, (dq) => dq.shooterId === shooterId)) return null;

          return (
            <Box key={shooterId} sx={{ mt: 3, overflowX: 'auto' }}>
              {/* Shooter header */}
              <Box sx={{ display: 'flex', fontWeight: 'bold' }}>
                <Box sx={{ width: '60%' }}>
                  <PublicId publicId={anyScore.shooterPublicId} name={anyScore.shooterName} isRo={anyScore.isRo} />
                </Box>
                <Box sx={{ width: '15%' }}>{t(`divisions.${toDivisionKey(anyScore.division)}`, { defaultValue: anyScore.division })}</Box>
                <Box sx={{ width: '15%' }}>{anyScore.category !== 'regular' ? t(`categories.${toCategoryKey(anyScore.category)}`, { defaultValue: anyScore.category }) : ''}</Box>
                <Box sx={{ width: '10%' }}>{t(`registration.${anyScore['power-factor'] || 'minor'}`, { defaultValue: anyScore['power-factor'] || 'minor' })}</Box>
              </Box>
              {/* Stage table */}
              <table border="1" style={{ width: '100%', marginTop: 6, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <td style={cellStyle}>{t('results.stageNumber')}</td>
                    <td style={cellStyle}>{t('results.time')}</td>
                    <td style={cellStyle}>A</td>
                    <td style={cellStyle}>{cHeader}</td>
                    <td style={cellStyle}>{dHeader}</td>
                    <td style={cellStyle}>M</td>
                    <td style={cellStyle}>(M)</td>
                    <td style={cellStyle}>PT</td>
                    <td style={cellStyle}>Proc</td>
                    <td style={cellStyle}>Ded</td>
                    <td style={cellStyle}>{t('results.totalPoints')}</td>
                    <td style={cellStyle}>{t('results.factor')}</td>
                  </tr>
                </thead>
                <tbody>
                  {perStage.map((result, idx) => {
                    if (!result) return null;
                    return (
                      <tr key={idx}>
                        <td style={cellStyle}>
                          {idx + 1}
                          {scoreCountMap[`${shooterId}_${idx + 1}`] > 1 && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              backgroundColor: '#d32f2f', color: 'white', borderRadius: '50%',
                              width: 18, height: 18, fontSize: 11, fontWeight: 700,
                              marginInlineStart: 4, verticalAlign: 'middle',
                            }}>
                              {scoreCountMap[`${shooterId}_${idx + 1}`]}
                            </span>
                          )}
                        </td>
                        <td style={cellStyle}>{result.timeStr}</td>
                        <td style={cellStyle}>{result.alphas}</td>
                        <td style={cellStyle}>{result.charlies}</td>
                        <td style={cellStyle}>{scoreMode === 'il-army' ? result.bonusField : result.deltas}</td>
                        <td style={cellStyle}>{result.misses}</td>
                        <td style={cellStyle}>{result.bonusMisses}</td>
                        <td style={cellStyle}>{result.noShoots}</td>
                        <td style={cellStyle}>{result.penalties}</td>
                        <td style={cellStyle}>{result.specialPenaltyPercentageStr}</td>
                        <td style={cellStyle}>{result.total}</td>
                        <td style={cellStyle}>{result.factorStr}</td>
                      </tr>
                    );
                  })}
                  {/* Totals row */}
                  <tr>
                    <td style={cellStyle}><b>{t('results.totalPoints')}</b></td>
                    <td style={{ ...cellStyle, opacity: 0.4 }}>{perStage.reduce((sum, s) => sum + (s?.time || 0), 0).toFixed(2)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.alphas || 0), 0)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.charlies || 0), 0)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.deltas || 0), 0)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.misses || 0), 0)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.bonusMisses || 0), 0)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.noShoots || 0), 0)}</td>
                    <td style={cellStyle}>{perStage.reduce((sum, s) => sum + (s?.penalties || 0), 0)}</td>
                    <td style={cellStyle} />
                    <td style={{ ...cellStyle, opacity: 0.4 }}>{perStage.reduce((sum, s) => sum + (s?.total || 0), 0)}</td>
                    <td style={cellStyle} />
                  </tr>
                </tbody>
              </table>
            </Box>
          );
        })}
      </Box>
    );
  };

  const matchTeams = match?.teams || [];
  const hasTeams = matchTeams.length > 0;

  const teamTypeLabel = (tt) => {
    if (!tt) return '';
    const parts = tt.split('-');
    if (parts.length === 1) {
      return t(`teams.type${tt.charAt(0).toUpperCase() + tt.slice(1)}`, tt);
    }
    const cat = parts[0];
    const div = parts.slice(1).join('-');
    const divLabel = t(`divisions.${toDivisionKey(div)}`, { defaultValue: div });
    if (cat === 'regular') return divLabel;
    return `${divLabel} - ${t(`categories.${toCategoryKey(cat)}`, { defaultValue: cat })}`;
  };

  const renderTeams = () => {
    const teamIds = matchTeams.filter((tm) => tm.type === teamType).map((tm) => tm.id);
    const results = {};
    scoresOverall.forEach((score) => {
      if (!teamIds.includes(score.shooterTeam)) return;
      results[score.shooterTeam] = results[score.shooterTeam] || { team: score.shooterTeam, scores: [] };
      results[score.shooterTeam].scores.push(score.score);
      results[score.shooterTeam].scores.sort((a, b) => b - a);
      results[score.shooterTeam].total = sum(results[score.shooterTeam].scores.slice(0, 3));
    });
    const sorted = sortBy(Object.values(results), (s) => -1 * s.total);

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Typography variant="h5" gutterBottom>
          {t('results.teamsTitle', { teamType: teamTypeLabel(teamType) })}
        </Typography>
        <Typography variant="subtitle2" sx={{ pb: 1.5 }}>
          {t('results.teamsSubtitle')}
        </Typography>
        <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <td style={{ padding: '6px' }} />
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.totalPoints')}</b></td>
              <td style={{ padding: '6px', textAlign: 'center' }}><b>{t('results.teamName')}</b></td>
            </tr>
          </thead>
          <tbody>
            {sorted.map((score, index) => (
              <tr key={score.team}>
                <td style={{ padding: '12px' }}>{index + 1}</td>
                <td style={{ padding: '6px' }}>{pad(score.total, 4)}</td>
                <td style={{ padding: '6px' }}>{matchTeams.find((tm) => tm.id === score.team)?.title}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  const renderTeamTypeSelector = () => {
    if (!hasTeams || scoreType !== 'teams') return null;
    const allDivs = getAllDivisions({ matchType: match.type });
    const allCats = getAllCategories({ matchType: match.type });
    return (
      <Select sx={{ mx: 1.5 }} value={teamType || ''} onChange={(e) => setTeamType(e.target.value)} variant="standard">
        {matchTeams.some((tm) => tm.type === 'generic') && (
          <MenuItem value="generic">{t('teams.typeGeneric')}</MenuItem>
        )}
        {allCats.flatMap((cat) => allDivs.map((div) => {
          const val = `${cat}-${div}`;
          if (!matchTeams.some((tm) => tm.type === val)) return null;
          return (
            <MenuItem key={val} value={val}>
              {t(`divisions.${toDivisionKey(div)}`, { defaultValue: div })}
              {cat !== 'regular' && <span>&nbsp;-&nbsp;{t(`categories.${toCategoryKey(cat)}`, { defaultValue: cat })}</span>}
            </MenuItem>
          );
        }))}
        {matchTeams.some((tm) => tm.type === 'family') && (
          <MenuItem value="family">{t('teams.typeFamily')}</MenuItem>
        )}
      </Select>
    );
  };

  const handleWhatIfReset = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('whatIf');
    next.delete('whatIfOverride');
    next.delete('shooterId');
    setSearchParams(next);
  };

  const handleWhatIfBack = () => {
    if (!whatIfShooterId) {
      navigate(`/${match.alias}/shooter-stats`);
      return;
    }
    const params = new URLSearchParams();
    if (whatIfParam) params.set('whatIf', whatIfParam);
    if (whatIfOverrideParam) params.set('whatIfOverride', whatIfOverrideParam);
    const qs = params.toString();
    navigate(`/${match.alias}/shooter-stats/${encodeURIComponent(whatIfShooterId)}${qs ? `?${qs}` : ''}`);
  };

  return (
    <Box sx={{ width: '90%', maxWidth: 720, mx: 'auto' }}>
      {/* What If banner */}
      {hasWhatIf && (
        <Paper
          className="noprint"
          sx={{
            bgcolor: '#d32f2f',
            color: 'white',
            p: 1.5,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
          elevation={4}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            {t('results.whatIfActive')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white', textTransform: 'none' }}
            onClick={handleWhatIfBack}
          >
            {t('results.whatIfBackToStats')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white', textTransform: 'none' }}
            onClick={handleWhatIfReset}
          >
            {t('results.whatIfResetAll')}
          </Button>
        </Paper>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }} className="noprint">
        <Select sx={{ mx: 1.5 }} value={scoreType} onChange={(e) => setScoreType(e.target.value)} variant="standard">
          {match.scoresPublished !== 'verify-only' && <MenuItem value="overall">{t('results.overall')}</MenuItem>}
          {match.scoresPublished !== 'verify-only' && <MenuItem value="by-stage">{t('results.byStage')}</MenuItem>}
          {match.scoresPublished !== 'verify-only' && <MenuItem value="by-category">{t('results.byCategory')}</MenuItem>}
          <MenuItem value="verify">{t('results.verify')}</MenuItem>
          {match.scoresPublished !== 'verify-only' && <MenuItem value="combined">{t('results.combined')}</MenuItem>}
          {match.scoresPublished !== 'verify-only' && <MenuItem value="combined-by-stage">{t('results.combinedByStage')}</MenuItem>}
          {hasTeams && match.scoresPublished !== 'verify-only' && <MenuItem value="teams">{t('results.teams')}</MenuItem>}
        </Select>

        {(scoreType === 'by-stage' || scoreType === 'combined-by-stage') && stages && (
          <Select sx={{ mx: 1.5 }} value={stage} onChange={(e) => setStage(e.target.value)} variant="standard">
            {stages.map((_, idx) => (
              <MenuItem key={idx} value={idx}>{t('results.stageN', { n: idx + 1 })}</MenuItem>
            ))}
            <MenuItem value="all">{t('results.allStages')}</MenuItem>
          </Select>
        )}

        {scoreType !== 'verify' && scoreType !== 'teams' && !combined && (
          <Select sx={{ mx: 1.5 }} value={division || ''} onChange={(e) => setDivision(e.target.value)} variant="standard">
            {divisions.map((d) => (
              <MenuItem key={d} value={d}>{t(`divisions.${toDivisionKey(d)}`, { defaultValue: d })}</MenuItem>
            ))}
            <MenuItem value="all">{t('results.allDivisions')}</MenuItem>
          </Select>
        )}

        {scoreType === 'by-category' && (
          <Select sx={{ mx: 1.5 }} value={category || categories[0] || ''} onChange={(e) => setCategory(e.target.value)} variant="standard">
            {getAllCategories({ matchType: match.type }).map((c) => (
              <MenuItem key={c} value={c}>{t(`categories.${toCategoryKey(c)}`, { defaultValue: c })}</MenuItem>
            ))}
            <MenuItem value="all">{t('results.allCategories')}</MenuItem>
          </Select>
        )}

        {renderTeamTypeSelector()}

        <IconButton onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
        </IconButton>
        <IconButton onClick={() => window.print()}>
          <PrintIcon />
        </IconButton>
        <IconButton onClick={handleExportCsv} title={t('common.export')}>
          <DownloadIcon />
        </IconButton>
      </Box>

      {/* Results Table */}
      {(scoreType === 'overall' || scoreType === 'combined') && renderOverall()}
      {(scoreType === 'by-stage' || scoreType === 'combined-by-stage') && renderByStage()}
      {scoreType === 'by-category' && renderByCategory()}
      {scoreType === 'verify' && renderVerify()}
      {scoreType === 'teams' && renderTeams()}

      {/* DQs */}
      {scoreType !== 'verify' && scoreType !== 'teams' && (
        <Box sx={{ pageBreakBefore: 'always' }}>
          <hr style={{ margin: '36px 0 24px 0', opacity: 0.2 }} />
          <Typography variant="h5" gutterBottom>{t('results.dqTitle')}</Typography>
          {dqs.length === 0 && (
            <Typography variant="body2">{t('results.noDqs')}</Typography>
          )}
          {divisions.map((div) => {
            const dqsInDiv = dqs.filter((dq) => dq.division === div);
            if (dqsInDiv.length === 0) return null;
            return (
              <Box key={div} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  {t(`divisions.${toDivisionKey(div)}`, { defaultValue: div })}
                </Typography>
                {sortBy(dqsInDiv, 'timestamp').map((dq) => (
                  <Box key={dq.shooterId} sx={{ display: 'flex', gap: 0.5 }}>
                    <PublicId publicId={dq.shooterPublicId} name={dq.shooterName} isRo={dq.isRo} />
                    <span>-</span>
                    <span>{new Date(dq.timestamp).toLocaleString()}</span>
                    {dq.category !== 'regular' && <span> ({t(`categories.${toCategoryKey(dq.category)}`, { defaultValue: dq.category })})</span>}
                    <span>-</span>
                    <span>{t('results.stageNumber')} {dq.stage}</span>
                    <span>-</span>
                    <span>{dq.dq}</span>
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
